/**
 * PendingSyncTray — header chip + drawer that shows queued writes saved by
 * the durable queue when the network/device fails. Users can manually
 * Retry or Discard each pending item.
 *
 * Visibility: chip appears whenever the current user has >=1 pending item.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, CloudOff, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import {
  onChange,
  getPending,
  manualRetry,
  discardItem,
} from '../lib/durableQueue';

function relTime(ms) {
  const diff = Math.max(0, Date.now() - ms);
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hr ago`;
  return `${Math.floor(diff / 86_400_000)} d ago`;
}

export default function PendingSyncTray() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const refresh = () => {
      if (!user?.id) { setItems([]); return; }
      getPending(user.id).then(setItems).catch(() => {});
    };
    refresh();
    const off = onChange(refresh);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const poll = setInterval(refresh, 5_000); // safety net
    return () => {
      off();
      clearInterval(poll);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [user?.id]);

  const count = items.length;

  // Don't render anything if nothing pending AND we're online
  if (count === 0 && online) return null;

  const hasErrors = items.some((i) => i.lastError && i.attempts >= 1);
  const chipColor = !online
    ? 'bg-amber-500/15 text-amber-700 border-amber-500/40 dark:text-amber-300'
    : hasErrors
    ? 'bg-red-500/15 text-red-700 border-red-500/40 dark:text-red-300'
    : 'bg-primary/15 text-primary border-primary/40';

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="pending-sync-chip"
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 text-xs font-bold uppercase tracking-wider press-scale ${chipColor}`}
          title={!online ? 'Offline — saving locally' : `${count} pending sync`}
        >
          {!online ? (
            <CloudOff className="w-3.5 h-3.5" strokeWidth={2.5} />
          ) : (
            <motion.span
              animate={count > 0 ? { rotate: 360 } : { rotate: 0 }}
              transition={{ duration: 2, repeat: count > 0 ? Infinity : 0, ease: 'linear' }}
            >
              <Cloud className="w-3.5 h-3.5" strokeWidth={2.5} />
            </motion.span>
          )}
          {count > 0 ? (
            <span data-testid="pending-sync-count">{count}</span>
          ) : (
            <span>Offline</span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="pending-sync-tray">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <Cloud className="w-5 h-5 text-primary" />
            Pending Sync
          </SheetTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {!online
              ? 'You are offline. Items below are saved on this device and will sync automatically when you reconnect.'
              : count === 0
              ? 'Everything is synced.'
              : 'These items are saved on this device and will sync as soon as the network allows. Nothing is lost.'}
          </p>
        </SheetHeader>

        <div className="space-y-3 max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
          <AnimatePresence>
            {items.map((item) => {
              const isBusy = busy === item.id;
              const blocked = !!item.blocked;
              return (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  className={`p-3 rounded-xl border-2 ${blocked ? 'border-red-500/40 bg-red-500/5' : 'border-border bg-card'}`}
                  data-testid={`pending-item-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{item.label || `${item.method} ${item.url}`}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Saved {relTime(item.created_at)} · {item.attempts || 0} attempt{item.attempts === 1 ? '' : 's'}
                      </p>
                      {item.lastError && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1 break-words" data-testid={`pending-error-${item.id}`}>
                          {blocked ? 'Server rejected — needs your attention: ' : 'Last error: '}
                          {item.lastError}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs rounded-full"
                        disabled={isBusy}
                        onClick={async () => {
                          setBusy(item.id);
                          await manualRetry(item.id);
                          setTimeout(() => setBusy(null), 1500);
                        }}
                        data-testid={`pending-retry-${item.id}`}
                      >
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        <span className="ml-1">Retry</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-red-500 rounded-full"
                        onClick={async () => {
                          if (!window.confirm('Discard this pending action? It cannot be recovered.')) return;
                          await discardItem(item.id);
                          toast.message('Discarded pending item');
                        }}
                        data-testid={`pending-discard-${item.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                        <span className="ml-1">Discard</span>
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {count === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              All clear — every action is synced.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
